import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { Archive } from '../api/archives';
import {
  downloadBlob,
  ExportCancelledError,
  exportRealtime,
  exportWithMediabunny,
  webCodecsAvailable,
  type CancelToken,
  type ExportProgress,
} from '../engine/exporter';
import type { PreviewEngine } from '../engine/preview';
import { validateEdit } from '../format/helpers';
import { useProject } from '../state/project';
import { useTimeline } from '../state/timeline';
import './ExportDialog.css';

interface Props {
  archives: Archive[];
  projectId: number;
  engineRef: MutableRefObject<PreviewEngine | null>;
  onClose: () => void;
}

const RESOLUTIONS = [
  { label: 'project size', value: 0 },
  { label: '1280 × 720', value: 720 },
  { label: '854 × 480', value: 480 },
];

export default function ExportDialog({ archives, engineRef, onClose }: Props) {
  const [container, setContainer] = useState<'mp4' | 'webm'>('mp4');
  const [resolution, setResolution] = useState(0);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const cancelRef = useRef<CancelToken>({ cancelled: false });
  // Names the downloaded file after the project rather than "export.mp4".
  const projectTitle = useProject((s) => s.project?.title);

  useEffect(
    () => () => {
      cancelRef.current.cancelled = true;
    },
    [],
  );

  async function startBrowser() {
    const edit = useTimeline.getState().edit;
    const errors = validateEdit(edit);
    if (errors.length) {
      setError(`Timeline invalid: ${errors[0]}`);
      return;
    }
    setError(null);
    setWarnings([]);
    setRunning(true);
    cancelRef.current = { cancelled: false };
    const scale = resolution === 0 ? 1 : resolution / edit.settings.height;
    const width = Math.round((edit.settings.width * scale) / 2) * 2;
    const height = Math.round((edit.settings.height * scale) / 2) * 2;
    try {
      let result;
      if (webCodecsAvailable()) {
        try {
          result = await exportWithMediabunny(
            edit,
            archives,
            { container, width, height, title: projectTitle, onProgress: setProgress },
            cancelRef.current,
          );
        } catch (e) {
          if (e instanceof ExportCancelledError) throw e;
          // fall back to realtime capture
          const engine = engineRef.current;
          if (!engine) throw e;
          setWarnings([
            `WebCodecs export failed (${e instanceof Error ? e.message : e}) — using realtime capture`,
          ]);
          result = await exportRealtime(engine, edit, { title: projectTitle, onProgress: setProgress }, cancelRef.current);
        }
      } else {
        const engine = engineRef.current;
        if (!engine) throw new Error('preview engine unavailable');
        result = await exportRealtime(engine, edit, { title: projectTitle, onProgress: setProgress }, cancelRef.current);
      }
      setWarnings((w) => [...w, ...result.warnings]);
      downloadBlob(result.blob, result.filename);
      setProgress(null);
      setRunning(false);
    } catch (e) {
      setRunning(false);
      setProgress(null);
      if (e instanceof ExportCancelledError) setError('Export cancelled');
      else setError(e instanceof Error ? e.message : 'Export failed');
    }
  }

  const pct = progress
    ? Math.round((progress.framesDone / Math.max(1, progress.framesTotal)) * 100)
    : 0;

  return (
    <div className="export-overlay" onClick={() => !running && onClose()}>
      <div className="export-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="export-head">
          <h2>Export</h2>
          <button className="ghost icon" onClick={onClose} disabled={running}>
            ×
          </button>
        </div>

        <p className="export-pro">
          Renders locally with WebCodecs — no queue, instant download.
        </p>

        <div className="export-form">
          <label className="field">
            container
            <select
              value={container}
              disabled={running}
              onChange={(e) => setContainer(e.target.value as 'mp4' | 'webm')}
            >
              <option value="mp4">mp4 (h264 + aac)</option>
              <option value="webm">webm (vp9 + opus)</option>
            </select>
          </label>
          <label className="field">
            resolution
            <select
              value={resolution}
              disabled={running}
              onChange={(e) => setResolution(parseInt(e.target.value, 10))}
            >
              {RESOLUTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          {!webCodecsAvailable() && (
            <p className="export-note">
              WebCodecs unavailable — will record in realtime via MediaRecorder (webm only).
            </p>
          )}
        </div>

        {(running || progress) && (
          <div className="export-progress">
            <div className="export-progress-bar">
              <div style={{ width: `${pct}%` }} />
            </div>
            <span className="timecode">
              {progress
                ? `${progress.phase} — ${progress.framesDone}/${progress.framesTotal}`
                : 'starting…'}
            </span>
          </div>
        )}

        {error && <p className="export-error">{error}</p>}
        {warnings.map((w, i) => (
          <p className="export-note" key={i}>
            {w}
          </p>
        ))}

        <div className="export-actions">
          {!running ? (
            <button className="primary" onClick={() => void startBrowser()}>
              Start export
            </button>
          ) : (
            <button
              className="danger"
              onClick={() => (cancelRef.current.cancelled = true)}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
